/**
 * Client workspace Library tab (Unit 7.IMPL.A, insurescope-7149k.3.1.3).
 *
 * The tab is a navigable FOLDER VIEW rooted at the client's root folder;
 * deliberately reversing Unit 2.5's flat client_id scoping (v2 plan
 * supersedes; brief: docs/design-briefs/2026-06-05-client-workspace-folder-view.md).
 * The listing-mode table (binding, insurescope-7149k.3.1):
 *
 *   browse, root        -> folder_id = root_folder_id   (folders shown)
 *   browse, subfolder N -> folder_id = N                (folders shown)
 *   search (q)          -> client_id + q, flat org-wide (folders hidden;
 *                          finds tagged-but-filed-elsewhere items)
 *   no root yet         -> NO fetch; the workspace-start empty state
 *
 * Browsing sends folder_id and STOPS sending client_id: folder rows
 * reappear server-side because show_folders = client_id is None. The mode
 * flip is which DocumentList prop gets passed; the tab owns the SINGLE
 * search debounce (LIBRARY_SEARCH_DEBOUNCE_MS) and hands the settled value
 * down with `searchQuerySettled`, so the flip and the requested result set
 * land in the same render by construction (insurescope-2h145; one-render
 * lag accepted per the brief).
 *
 * `?folder=` itself is owned by ClientDetail (validate-then-set beside
 * `?tab=`); this component receives the VALIDATED location and reports
 * navigation/stale-folder events back up. A browse-mode 404 on a
 * cache-validated folder is the mid-session-deletion signal
 * (onFolderUnavailable -> toast + root fallback in the shell).
 *
 * Clicking a document row opens the shared DocumentDetailLayout panel;
 * the same detail surface as the global Document Library (the layout and
 * panel live in @/components/frontend/documents).
 *
 * The New menu's dialogs stay client-aware (insurescope-xcmui) and target
 * the NAVIGATED folder (R4, 7b; insurescope-7149k.3.1.5): uploads carry
 * `defaultClient` + folderId = the current folder; New Folder parents the
 * dialog at the current folder directly when a root exists, and falls back
 * to the resolve-then-act verb only for the no-root first write. Workspace
 * breadcrumbs ride FilterBar's caller-supplied-segments seam (root crumb =
 * client name; ?folder= navigation only; never a router exit).
 */
import { getClient, getLibraryFolders } from "@/api";
import { DocumentDropZone } from "@/components/frontend/DocumentDropZone";
import { DocumentDetailLayout } from "@/components/frontend/documents/DocumentDetailLayout";
import { buildClientRootedBreadcrumbs } from "@/components/shared/document-picker/clientBreadcrumbs";
import type { IBreadcrumbSegment } from "@/components/shared/document-picker/DocumentPicker.types";
import { DocumentListEmptyState } from "@/pages/frontend/documents/components/DocumentListEmptyState";
import { CreateFolderDialog } from "@/pages/frontend/documents/components/CreateFolderDialog";
import { DocumentList } from "@/pages/frontend/documents/components/DocumentList";
import { FilterBar } from "@/pages/frontend/documents/components/FilterBar";
import { UploadDialog } from "@/pages/frontend/documents/components/UploadDialog";
import { LIBRARY_SEARCH_DEBOUNCE_MS } from "@/pages/frontend/documents/constants";
import { useScopesEnabled } from "@/contexts/frontend/organization/useScopesEnabled";
import { getClientSubtreeFolders } from "@/pages/frontend/documents/utils/folderTreeUtils";
import {
    LibraryTypeFilter,
    parseTypeFilter,
} from "@/pages/frontend/documents/utils/libraryItemTypes";
import { useDebouncedEffect, useMediaQuery } from "@react-hookz/web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ReactNode, useCallback, useMemo, useState } from "react";

import { useResolveClientRootFolder } from "../hooks/useResolveClientRootFolder";

export interface ClientLibraryTabProps {
    clientId: number;
    /** root_folder_id from the detail read (Unit 3); null = no workspace
     * folder materialized yet. */
    rootFolderId: number | null;
    /** The VALIDATED current folder (equals rootFolderId at the root).
     * Owned and validated by ClientDetail via ?folder=. */
    currentFolderId: number | null;
    /** Both the client and the folders cache have loaded. Until then the
     * tab must not fetch: ?folder= can't be validated yet, and a
     * premature root fetch is the fallback flash. */
    folderStateReady: boolean;
    onFolderNavigate: (folderId: number | null) => void;
    /** The browse listing 404'd on a cache-validated folder (mid-session
     * deletion); the shell toasts and falls back to root. */
    onFolderUnavailable: () => void;
}

export const ClientLibraryTab = ({
    clientId,
    rootFolderId,
    currentFolderId,
    folderStateReady,
    onFolderNavigate,
    onFolderUnavailable,
}: ClientLibraryTabProps) => {
    const scopesEnabled = useScopesEnabled();
    const queryClient = useQueryClient();

    // DnD is a desktop pointer affordance (Unit 8); the menu ->
    // MoveToFolderModal path is the keyboard/mobile move parity. Same
    // breakpoint source as FilterBar/DocumentLibraryContent. Coerced to
    // false on the first (undefined) pass so the desktop split-pane is the
    // default, matching DocumentDetailLayout's host contract.
    const isMobile = useMediaQuery("(max-width: 768px)") ?? false;

    const [searchValue, setSearchValue] = useState("");
    const [selectedTypeFilter, setSelectedTypeFilter] =
        useState<LibraryTypeFilter>("all");
    const typeFilter =
        parseTypeFilter(selectedTypeFilter, scopesEnabled) ?? "all";
    if (typeFilter !== selectedTypeFilter) {
        setSelectedTypeFilter(typeFilter);
    }
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
    // Files dropped onto the page-level drop zone before the dialog opened;
    // cleared on close and on root-resolution failure so they never leak into
    // a subsequent unrelated open.
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);

    // Row click opens the shared detail panel (parity with the document
    // library). Re-click toggles it closed; the panel self-fetches, so it
    // survives folder navigation and search-mode flips until dismissed.
    const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
        null
    );
    const handleDocumentSelect = useCallback((id?: number) => {
        if (!id) {
            setSelectedDocumentId(null);
            return;
        }
        setSelectedDocumentId((prev) => (prev === id ? null : id));
    }, []);

    // The SINGLE search debounce (insurescope-2h145): the tab owns the one
    // timer and hands the settled value to DocumentList below
    // (`searchQuerySettled` skips its internal re-debounce), so the MODE
    // flip and the requested result set derive from the same settled value;
    // coherent by construction, not by timer alignment.
    const [debouncedSearch, setDebouncedSearch] = useState(searchValue);
    useDebouncedEffect(
        () => setDebouncedSearch(searchValue),
        [searchValue],
        LIBRARY_SEARCH_DEBOUNCE_MS
    );
    const searchMode = debouncedSearch.trim().length > 0;

    // Folder navigation is a browse-intent declaration: it decisively
    // cancels pending search (insurescope-969nt). Without this, a click
    // inside the debounce window leaves the stale timer alive, which flips
    // the surface to flat search AFTER the clicked folder loads,
    // persistently, since the input still holds the abandoned fragment.
    // Both halves clear: the input value (also re-arms the trailing timer
    // with "") and the settled value (covers an already-flipped mode).
    const handleFolderNavigate = useCallback(
        (folderId: number | null) => {
            setSearchValue("");
            setDebouncedSearch("");
            onFolderNavigate(folderId);
        },
        [onFolderNavigate]
    );

    // Same key as the main library (shares its cache); needed by FilterBar's
    // breadcrumb drop-validation and the dialogs' folder pickers. Memoized
    // so the `?? []` fallback can't churn the breadcrumb memo every render.
    const { data: foldersData, refetch: refetchFolders } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
    });
    const folders = useMemo(
        () => foldersData?.folders ?? [],
        [foldersData?.folders]
    );

    // Same key as ClientDetail's query; served from its warm cache. The New
    // menu's dialogs need the client (id + name) so content created here is
    // pre-tagged, i.e. actually visible in this tab (insurescope-xcmui).
    const { data: client } = useQuery({
        queryKey: ["client", clientId],
        queryFn: () => getClient(clientId),
    });

    // Creation targeting (R4, 7b): with a root on file, New Folder parents
    // the dialog at the NAVIGATED folder directly; no resolve round-trip
    // (the root demonstrably exists). The resolve-then-act verb survives
    // only for the no-root first write, where it materializes the root and
    // invalidates ["client", id] so the tab flips to browse.
    const [createFolderParentId, setCreateFolderParentId] = useState<
        number | null
    >(null);
    const resolveRootForFolder = useResolveClientRootFolder(clientId, {
        onResolved: (folder) => {
            setCreateFolderParentId(folder.id);
            setCreateFolderDialogOpen(true);
        },
        errorFallback: "Couldn't create the folder",
    });
    const openCreateFolder = () => {
        if (rootFolderId !== null) {
            setCreateFolderParentId(currentFolderId ?? rootFolderId);
            setCreateFolderDialogOpen(true);
        } else {
            resolveRootForFolder.mutate();
        }
    };

    // Upload is the same resolve-then-act verb (insurescope-pbu9a): a
    // client-tagged upload with folder_id null is sanctioned tag-without-
    // placement server-side: it files at the LIBRARY root and materializes
    // nothing, so the document would be invisible to browse mode and the
    // workspace-start state would never flip. The target is captured as
    // state (not read live from props at submit): the resolve's ["client"]
    // invalidation flips rootFolderId asynchronously.
    const [uploadFolderId, setUploadFolderId] = useState<number | null>(null);
    const resolveRootForUpload = useResolveClientRootFolder(clientId, {
        onResolved: (folder) => {
            setUploadFolderId(folder.id);
            setUploadDialogOpen(true);
        },
        errorFallback: "Couldn't start the upload",
    });
    const openUpload = () => {
        if (rootFolderId !== null) {
            setUploadFolderId(currentFolderId ?? rootFolderId);
            setUploadDialogOpen(true);
        } else {
            resolveRootForUpload.mutate();
        }
    };
    // Called by DocumentDropZone; stashes files then opens via the same flow.
    // Per-call onError clears the stash on resolution failure so the files
    // never leak into a later unrelated open.
    const handleDropFiles = useCallback(
        (files: File[]) => {
            setDroppedFiles(files);
            if (rootFolderId !== null) {
                setUploadFolderId(currentFolderId ?? rootFolderId);
                setUploadDialogOpen(true);
            } else {
                resolveRootForUpload.mutate(undefined, {
                    onError: () => setDroppedFiles([]),
                });
            }
        },
        [rootFolderId, currentFolderId, resolveRootForUpload]
    );

    const handleContentChange = () => {
        refetchFolders();
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
        // root_folder_id (and the header counts) live on the client read; a
        // first write may have just materialized the root, flipping the
        // workspace-start state to browse.
        queryClient.invalidateQueries({ queryKey: ["client", clientId] });
    };

    // Workspace breadcrumbs (7b) ride FilterBar's caller-supplied-segments
    // seam: param-based, root crumb = the CLIENT name, zero router navigation
    // (no crumb can exit to the document library). Shape and drop-id rules
    // live in buildClientRootedBreadcrumbs.
    const workspaceBreadcrumbs: IBreadcrumbSegment[] = useMemo(
        () =>
            buildClientRootedBreadcrumbs({
                folders,
                rootFolderId,
                currentFolderId,
                clientName: client?.name,
                searchMode,
                onNavigate: handleFolderNavigate,
            }),
        [
            searchMode,
            client?.name,
            rootFolderId,
            currentFolderId,
            folders,
            handleFolderNavigate,
        ]
    );

    // Browse-mode 404 on a non-root folder = the folder died mid-session
    // (the cache validated it; the listing knows better). Root 404s are
    // structurally impossible (client roots can't be deleted, Unit 1) and
    // search-mode errors keep DocumentList's own error treatment.
    const handleListError = useCallback(
        (err: unknown) => {
            if (searchMode) return;
            if (currentFolderId === null || currentFolderId === rootFolderId)
                return;
            if (!isAxiosError(err) || err.response?.status !== 404) return;
            onFolderUnavailable();
        },
        [searchMode, currentFolderId, rootFolderId, onFolderUnavailable]
    );

    // Built inside the detail-pane layout's render prop because the list
    // depends on `panelResizeTick` (forwarded to DocumentList's virtualizer
    // so it re-measures when the panel resizes).
    const renderListArea = (panelResizeTick: number): ReactNode => {
        if (!folderStateReady) {
            // The ?folder= validation gate: no fetch, no fallback flash. The
            // frame holds the surface's shape; DocumentList's own skeleton
            // takes over on the true first load.
            return (
                <div className="min-h-64 overflow-hidden rounded-md border" />
            );
        }
        if (!searchMode && rootFolderId === null) {
            // No root yet: the designed first-run moment (brief §1). No fetch:
            // there is nothing to list; search still works (flat mode reaches
            // tagged-but-filed-elsewhere items even before the root exists).
            return (
                <div className="overflow-hidden rounded-md border">
                    <DocumentListEmptyState
                        variant="workspace-start"
                        clientName={client?.name ?? "this client"}
                        onUpload={openUpload}
                        onCreateFolder={openCreateFolder}
                    />
                </div>
            );
        }
        return (
            <DocumentList
                activeTab="documents"
                folderId={searchMode ? null : currentFolderId}
                clientId={searchMode ? clientId : null}
                moveClientId={clientId}
                searchQuery={debouncedSearch}
                searchQuerySettled
                typeFilter={typeFilter}
                onSearchClear={() => setSearchValue("")}
                onFolderNavigate={handleFolderNavigate}
                onListError={handleListError}
                onDocumentAction={handleContentChange}
                onUpload={openUpload}
                onDocumentSelect={handleDocumentSelect}
                selectedDocumentId={selectedDocumentId}
                panelResizeTick={panelResizeTick}
                enableDragAndDrop={!isMobile}
                folders={folders}
                hideClientColumn
                clearSelectionOnResultSetChange
            />
        );
    };

    // Overlay title is folder-aware: inside a subfolder show the folder name,
    // at the client root (or before the root exists) show the client name.
    const currentFolder =
        currentFolderId !== null && currentFolderId !== rootFolderId
            ? folders.find((f) => f.id === currentFolderId)
            : null;
    const dropZoneTitle = `Upload files to ${currentFolder?.name ?? client?.name ?? "this workspace"}`;

    return (
        <>
            <DocumentDropZone
                title={dropZoneTitle}
                subtitle="Drop PDF, Excel or CSV files here"
                onDropFiles={handleDropFiles}
                allowSpreadsheets
                // Inert while the dialog is open (re-drops go to the inner
                // dropzone) and while root resolution is pending.
                disabled={uploadDialogOpen || resolveRootForUpload.isPending}
                // Bleed across the pane scrollport's pt/pb/pl-4 (mirrored)
                // so the overlay reaches its visual edges; flex-1 keeps the
                // drop target filling the pane even when the listing is
                // short.
                className="-my-4 -ml-4 flex min-h-0 flex-1 flex-col py-4 pl-4"
            >
                <DocumentDetailLayout
                    isMobile={isMobile}
                    selectedDocumentId={selectedDocumentId}
                    onClose={() => setSelectedDocumentId(null)}
                >
                    {(panelResizeTick) => (
                        <>
                            <FilterBar
                                activeTab="documents"
                                hideTabs
                                hideTitle
                                searchValue={searchValue}
                                onSearchChange={setSearchValue}
                                onSearchClear={() => setSearchValue("")}
                                typeFilter={typeFilter}
                                onTypeFilterChange={setSelectedTypeFilter}
                                onUploadClick={openUpload}
                                onCreateFolderClick={openCreateFolder}
                                currentFolder={null}
                                breadcrumbSegments={workspaceBreadcrumbs}
                                folders={folders}
                                // Breadcrumb-drop parity with the list rows
                                // (g5bqp): the forward leg refreshes via
                                // onDropComplete, the undo leg has no callback
                                // and rides moveClientId into the engine.
                                onDropComplete={handleContentChange}
                                moveClientId={clientId}
                            />

                            {renderListArea(panelResizeTick)}
                        </>
                    )}
                </DocumentDetailLayout>
            </DocumentDropZone>

            <UploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    setDroppedFiles([]);
                }}
                // Creation targeting (R4): uploads land in the folder being
                // looked at, captured at open time by openUpload; the
                // no-root flow resolves the root first (insurescope-pbu9a),
                // so the placement is never null.
                folderId={uploadFolderId}
                isKnowledgeBase={false}
                allowSpreadsheets
                // Client-scoped (R5): only this client's subtree is a valid
                // destination, and an empty pick (current folder = root) lands
                // in the client root, not the org library root.
                folders={getClientSubtreeFolders(folders, rootFolderId)}
                rootFolderId={rootFolderId}
                onUploadComplete={handleContentChange}
                defaultClient={client ?? null}
                initialFiles={
                    droppedFiles.length > 0 ? droppedFiles : undefined
                }
            />

            <CreateFolderDialog
                open={createFolderDialogOpen}
                onClose={() => setCreateFolderDialogOpen(false)}
                onCreated={() => {
                    setCreateFolderDialogOpen(false);
                    handleContentChange();
                }}
                parentId={createFolderParentId}
                parentClient={client ?? null}
                // R6: inside the workspace the client is implicit, so no client
                // select; the new folder is always tagged to this client.
                lockClient
            />
        </>
    );
};
